import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";

import { Picture } from "@/components/wpl/image/src";
import { wacToken } from "@/libraries/design/foundations";

export interface AvatarFacepileProps {
    icons: string[];
    max: number;
    overlap?: number;
    iconSize?: number;
}

export const AvatarIcon = ({
    icon,
    iconSize,
    overlap
}: {
    icon: string;
    iconSize: number;
    overlap: number;
}) => {
    const iconStyles = css({
        borderRadius: "60px",
        display: "inline-block",
        height: `${iconSize}px`,
        marginInlineStart: `-${overlap}px`,
        overflow: "hidden",
        position: "relative",
        width: `${iconSize}px`,
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        img: {
            verticalAlign: "super"
        }
    });

    return (
        <Picture
            css={iconStyles}
            image={{
                src: icon,
                alt: "avatar",
                width: iconSize,
                height: iconSize
            }}
        />
    );
};

export const AvatarFacepile = ({
    icons,
    max,
    overlap = 4,
    iconSize = 16
}: AvatarFacepileProps) => {
    const userCountPillStyles = css({
        alignItems: "center",
        borderRadius: "66px",
        // eslint-disable-next-line local/ensure-wac-token-usage
        border: "0.67px solid #0B122824",
        color: wacToken("color.neutral.light800"),
        display: "inline-flex",
        fontSize: "9.33px",
        fontWeight: 400,
        gap: token("space.0"),
        height: `${iconSize}px`,
        justifyContent: "center",
        lineHeight: `${iconSize}px`,
        marginInlineStart: `${overlap * -1}px`,
        padding: `${token("space.0")} ${token("space.075")} ${token(
            "space.0"
        )} ${token("space.075")}`,
        textAlign: "center",
        verticalAlign: "middle",
        width: "auto"
    });

    const facepileStyles = [
        css({
            display: "inline-flex",
            height: `${iconSize}px`,
            width: "100%",
            // For some reason negative margin was not working dynamically on the child itself
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            div: {
                marginInlineStart: `-${overlap}px`
            }
        }),
        overlap > 0 &&
            css({
                marginInlineStart: `${overlap}px`
            })
    ];

    return (
        <div css={facepileStyles}>
            {icons.slice(0, max).map((icon, index) => (
                <AvatarIcon
                    key={index}
                    icon={icon}
                    iconSize={iconSize}
                    overlap={overlap}
                />
            ))}
            {icons.length > max && (
                <span css={userCountPillStyles}>+{icons.length - max}</span>
            )}
        </div>
    );
};
